import { useTranslation } from "react-i18next";
import TimelineCard from "./components/TimelineCard";
import { URLS } from "../../../../services/urls";
import { useCallback, useEffect, useRef, useState } from "react";
import { styles } from "../../../../components/utils/reactSelect";
import { API } from "../../../../services/axios";
import { useSelector } from "react-redux";
import useSWR from "swr";
import CreatableSelect from "react-select/creatable";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { confirmDeletion } from "../../../../components/dletion";
import Select from "react-select";
import { ErrorMessage } from "@hookform/error-message";
import moment from "moment";

const ManageEducation = ({ handleNext, handlePrev }) => {
  const { t }      = useTranslation();
  const { action } = useParams();
  const { auth }   = useSelector((state) => state.auth);

  const [educations, setEducations]     = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [isLoading, setIsLoading]       = useState(false);
  const [toEdit, setToEdit]             = useState(null);
  const [loading, setLoading]           = useState(false);
  const [search, setSearch]             = useState("");
  const [searchDiploma, setSearchDiploma] = useState("");
  const [selected, setSelected]         = useState(null);
  const [selectedDiploma, setSelectedDiploma] = useState(null);

  const { data: diploma,  isLoading: loadingDiploma  } = useSWR(URLS.CONFIGS.PROFILE.DIPLOMA.list(searchDiploma));
  const { data: colleges, isLoading: loadingColleges } = useSWR(URLS.CONFIGS.PROFILE.COLLEGE.list(search));

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({});
  const formRef = useRef(null);

  // ─── Fetch educations, filter client-side by profile ─────────────────────
  const loadEducations = useCallback(() => {
    if (!action || action === "create") return;
    setFetchLoading(true);
    API.get(URLS.USERS.PROFILE.details(action))
      .then(({ data }) => {
        setEducations(data.education || []);
        setFetchLoading(false);
      })
      .catch(() => setFetchLoading(false));
  }, [action]);

  useEffect(() => {
    loadEducations();
  }, [loadEducations]);

  // ─── Edit ──────────────────────────────────────────────────────────────────
  const handleChangeEducation = (education) => {
    setToEdit(education);
    reset({
      educationFrom:        education.start_date,
      currentlyWorking:     !education.end_date,
      educationTo:          education.end_date,
      educationfieldOfStudy: education.field_of_study,
      educationGpa:         education.gpa,
    });
    setSelected({ label: education.college?.name, value: education.college?.id });
    setSelectedDiploma({ label: education.degree?.name || education.degree, value: education.degree?.id || education.degree });
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // ─── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteEducation = (education) => {
    const onSuccess = () =>
      setEducations((prev) => prev.filter((e) => e.id !== education.id));
    confirmDeletion(URLS.USERS.PROFILE.EDUCATION.update(education.id), t, onSuccess);
  };

  // ─── Submit ────────────────────────────────────────────────────────────────
  const onSubmit = (data) => {
    if (!selected || !selectedDiploma) {
      toast.error(t("textDegreeOrSchoolRequired") || "Diplôme ou école requis");
      return;
    }
    setLoading(true);
    const obj = {
      start_date:    data.educationFrom,
      end_date:      data.currentlyWorking ? null : data.educationTo,
      field_of_study: data.educationfieldOfStudy,
      degree:        selectedDiploma.value,
      gpa:           data.educationGpa !== "" ? data.educationGpa : undefined,
      college:       selected.value,
      profile:       action,
    };

    if (!toEdit) {
      API.post(URLS.USERS.PROFILE.EDUCATION.create, obj)
        .then(({ data: resp }) => {
          setLoading(false);
          reset();
          toast.success(t("educationAdded"));
          // Enrich with label info for display
          setEducations((prev) => [
            { ...resp, college: { name: selected.label, id: selected.value }, degree: { name: selectedDiploma.label, id: selectedDiploma.value } },
            ...prev,
          ]);
          setSelected(null);
          setSelectedDiploma(null);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.detail);
          setLoading(false);
        });
    } else {
      API.patch(URLS.USERS.PROFILE.EDUCATION.update(toEdit.id), obj)
        .then(({ data: resp }) => {
          setLoading(false);
          toast.success(t("educationEdited"));
          setEducations((prev) =>
            prev.map((ed) =>
              ed.id === toEdit.id
                ? { ...resp, college: { name: selected.label }, degree: { name: selectedDiploma.label } }
                : ed
            )
          );
          setToEdit(null);
          reset();
          setSelected(null);
          setSelectedDiploma(null);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.detail);
          setLoading(false);
        });
    }
  };

  // ─── College / Diploma creation ────────────────────────────────────────────
  const handleCreateCollege = (inputValue) => {
    setIsLoading(true);
    API.post(URLS.CONFIGS.PROFILE.COLLEGE.create, { name: inputValue, created_by: auth?.user?.id })
      .then(({ data }) => {
        setSelected({ label: data.name, value: data.id });
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  const handleCreateDiploma = (inputValue) => {
    API.post(URLS.CONFIGS.PROFILE.DIPLOMA.create, { name: inputValue, created_by: auth?.user?.id })
      .then(({ data }) => setSelectedDiploma({ label: data.name, value: data.id }))
      .catch(() => {});
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="panel-white mb-30" id="education">
      <div className="box-padding">
        <h5 className="icon-edu">{t("titleEducation")}</h5>
        <div className="row mt-30">
          <div className="col-lg-9">
            <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
              <div className="row">
                {/* Start date */}
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">
                      {t("educationDe")} <span className="text-primary">*</span>
                    </label>
                    <input
                      {...register("educationFrom", { required: t("textFormFieldRequired") })}
                      className="form-control"
                      type="date"
                    />
                    <ErrorMessage errors={errors} name="educationFrom"
                      render={({ message }) => <span className="text-primary">{message}</span>}
                    />
                  </div>
                </div>

                {/* End date */}
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">{t("educationTo")}</label>
                    {!watch("currentlyWorking") && (
                      <input
                        {...register("educationTo")}
                        className="form-control"
                        type="date"
                        max={moment().format("YYYY-MM-DD")}
                      />
                    )}
                    <div className="login_footer form-group mt-10">
                      <label className="cb-container">
                        <input type="checkbox" {...register("currentlyWorking")} />
                        <span className="text-small">{t("currentlyStudying")}</span>
                        <span className="checkmark" />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Degree */}
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">
                      {t("educationDegree")} <span className="text-primary">*</span>
                    </label>
                    <Select
                      styles={styles}
                      isDisabled={loadingDiploma}
                      inputId="select-diploma"
                      value={selectedDiploma}
                      onChange={(v) => setSelectedDiploma(v)}
                      options={diploma?.results?.map((i) => ({ label: i.name, value: i.id })) ?? []}
                      placeholder={t("textChoose")}
                    />
                  </div>
                </div>

                {/* GPA */}
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">{t("educationGpa")}</label>
                    <input
                      {...register("educationGpa")}
                      className="form-control"
                      type="number"
                      min={0}
                      step="0.01"
                      placeholder="3.8"
                    />
                  </div>
                </div>

                {/* Field of study */}
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">
                      {t("educationfieldOfStudy")} <span className="text-primary">*</span>
                    </label>
                    <input
                      {...register("educationfieldOfStudy", { required: t("textFormFieldRequired") })}
                      className="form-control"
                      type="text"
                      placeholder={t("educationfieldOfStudy")}
                    />
                    <ErrorMessage errors={errors} name="educationfieldOfStudy"
                      render={({ message }) => <span className="text-primary">{message}</span>}
                    />
                  </div>
                </div>

                {/* School */}
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">
                      {t("educationCollege")} <span className="text-primary">*</span>
                    </label>
                    <CreatableSelect
                      styles={styles}
                      isSearchable
                      isDisabled={isLoading}
                      inputId="select-college"
                      value={selected}
                      isLoading={isLoading || loadingColleges}
                      onInputChange={(v) => setSearch(v)}
                      onCreateOption={handleCreateCollege}
                      onChange={(v) => setSelected(v)}
                      options={colleges?.results?.map((i) => ({ label: i.name, value: i.id })) ?? []}
                      placeholder={t("textChoose")}
                    />
                  </div>
                </div>

                {/* Submit */}
                <div className="col-lg-12">
                  <div className="form-group mt-10 d-flex">
                    <button type="submit" className="btn btn-default btn-brand icon-tick ml-10">
                      {!toEdit ? t("textAddEducation") : t("textEditEducation")}
                      {loading && <span className="spinner-border spinner-border-sm ml-10" role="status" />}
                    </button>
                    {toEdit && (
                      <button
                        type="button"
                        className="btn btn-secondary ml-10"
                        onClick={() => { setToEdit(null); reset(); setSelected(null); setSelectedDiploma(null); }}
                      >
                        {t("deleteActionCancel")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>

            {/* List */}
            {fetchLoading ? (
              <div className="d-flex mt-30">
                <div className="spinner-border m-auto spinner-border-sm" role="status" />
              </div>
            ) : (
              <div className="box-timeline mt-50">
                {educations.map((education) => (
                  <TimelineCard
                    key={education.id}
                    handleChange={handleChangeEducation}
                    handleDelete={handleDeleteEducation}
                    education={education}
                  />
                ))}
              </div>
            )}

            {/* Navigation */}
            <div className="col-lg-12 mt-3">
              <div className="form-group mt-10 d-flex">
                <button type="button" onClick={handlePrev} className="btn btn-secondary btn-brand line-height-24">
                  {t("textPrevious")}
                </button>
                <button type="button" onClick={handleNext} className="btn btn-default btn-brand ml-auto">
                  {t("textNext")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageEducation;
